import { Component } from '@theme/component';

/**
 * "Compartir" control of the product page (`snippets/pc-share.liquid`).
 *
 * The control is a native `<details>`: the menu and its three links work without JavaScript. This component adds what
 * HTML cannot do alone:
 * - Escape closes the menu and returns the focus to the button, and so do a click outside and moving the focus out;
 * - "Copiar enlace" copies the canonical URL to the clipboard and says so in the live region (the button is hidden until
 *   this script runs, because without it there is nothing for the button to do);
 * - on a narrow screen with the Web Share API, the button opens the share menu of the device instead of the dropdown.
 *
 * Listeners are declarative (`on:keydown`, `on:click`, `on:focusout`) or live in one AbortController that is cleaned up on
 * disconnect, and the clipboard feedback timer is cleared too, so a section re-render leaves nothing behind.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The disclosure that holds the menu.
 * @property {HTMLElement} summary - The "Compartir" button.
 * @property {HTMLButtonElement} [copy] - The "Copiar enlace" button.
 * @property {HTMLElement} [copyLabel] - The text of the copy button.
 * @property {HTMLElement} [status] - Live region that announces the copy.
 *
 * @extends {Component<Refs>}
 */
class PcShare extends Component {
  requiredRefs = ['details', 'summary'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    document.addEventListener('click', this.#onDocumentClick, { signal: this.#abort.signal });

    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    window.clearTimeout(this.#timer);
  }

  updatedCallback() {
    super.updatedCallback();

    this.#enhance();
  }

  /** The copy button needs the clipboard, so it shows only when the browser has it. */
  #enhance() {
    const { copy } = this.refs;
    if (copy) copy.hidden = !navigator.clipboard?.writeText;
  }

  /**
   * On a narrow screen with the Web Share API the button opens the device's share menu instead of the dropdown.
   * @param {Event} event
   */
  async handleSummaryClick(event) {
    if (typeof navigator.share !== 'function' || !window.matchMedia('(max-width: 749px)').matches) return;

    event.preventDefault();
    try {
      await navigator.share({ title: this.dataset.title, text: this.dataset.text, url: this.dataset.url });
    } catch {
      // The visitor closed the share sheet: nothing to do
    }
  }

  /**
   * Escape closes the menu and gives the focus back to the button.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.refs.details.open) return;

    event.preventDefault();
    this.#close();
    this.refs.summary.focus();
  }

  /**
   * Closes the menu when the focus moves to something outside the control.
   * @param {FocusEvent} event
   */
  handleFocusOut(event) {
    const next = event.relatedTarget;
    if (next instanceof Node && !this.contains(next)) this.#close();
  }

  /** Copies the address and announces it. Called by the "Copiar enlace" button. */
  async copyLink() {
    const { copyLabel, status } = this.refs;

    try {
      await navigator.clipboard.writeText(this.dataset.url ?? '');
    } catch {
      return;
    }

    const copied = this.dataset.copiedLabel ?? '';
    if (copyLabel) copyLabel.textContent = copied;
    if (status) status.textContent = copied;

    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      if (copyLabel) copyLabel.textContent = this.dataset.copyLabel ?? '';
      if (status) status.textContent = '';
      this.#close();
    }, 1500);
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.refs.details.open && event.target instanceof Node && !this.contains(event.target)) this.#close();
  };

  #close() {
    this.refs.details.open = false;
  }
}

if (!customElements.get('pc-share')) {
  customElements.define('pc-share', PcShare);
}
